/* One required fee per eligible cart line, independent of that line's quantity.
 * Shopify checkout validation is still required for enforcement outside the theme.
 */
(() => {
  if (window.HerculesLabourFee) return;
  const script = document.currentScript;
  const root = window.Shopify?.routes?.root || '/';
  const cartURL = new URL(`${root}cart`, window.location.origin);
  const nativeFetch = window.fetch.bind(window);
  const dataSection = 'labour-fee-data';
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const errorMessage = 'We couldn’t update the required fees. Please try again before checking out.';
  let queue = Promise.resolve();
  const activeXHR = new Set();
  let checkingOut = false;
  let alert;

  function mutation(input, method = 'GET') {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
      return url.origin === location.origin && method.toUpperCase() === 'POST' &&
        /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  }

  // Serialize fetch mutations and fee repairs, so later adds/removals cannot undo an earlier repair.
  function enqueue(task) {
    const result = queue.catch(() => {}).then(task);
    queue = result;
    return result;
  }

  function showError(error) {
    console.error('[labour-fee]', error);
    if (!alert) {
      alert = document.createElement('div');
      alert.dataset.labourFeeError = '';
      alert.setAttribute('role', 'alert');
      alert.style.cssText = 'position:fixed;bottom:1rem;left:1rem;right:1rem;z-index:10000;background:white;color:#222;border:2px solid #b00020;padding:1rem;box-shadow:0 2px 12px #0003';
      const text = document.createElement('span');
      text.textContent = `${errorMessage} `;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Retry';
      retry.addEventListener('click', () => sync().catch(() => {}));
      alert.append(text, retry);
      document.body.append(alert);
    }
    alert.hidden = false;
  }

  async function sections(ids) {
    const result = {};
    // Shopify permits up to five sections per request.
    for (let start = 0; start < ids.length; start += 5) {
      const url = new URL(cartURL);
      url.searchParams.set('sections', ids.slice(start, start + 5).join(','));
      // /cart negotiates ordinary cart JSON when Accept is application/json,
      // even with ?sections=. Keep the HTML route so Shopify renders the sections envelope.
      const response = await nativeFetch(url.href, { cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Could not read cart sections');
      Object.assign(result, await response.json());
    }
    return result;
  }

  async function readState() {
    const html = (await sections([dataSection]))[dataSection];
    const data = new DOMParser().parseFromString(html || '', 'text/html').querySelector('[data-labour-fee]');
    // Missing/null section is an error, never an empty cart (which would remove valid fees).
    if (!data) throw new Error('Labour fee data section is missing');
    const state = JSON.parse(data.textContent);
    if (!Array.isArray(state.eligible) || !Array.isArray(state.fees) || !Array.isArray(state.cart?.items)) {
      throw new Error('Invalid labour fee data');
    }
    if (state.eligible.some(line => !line.valid || !line.feeVariantId)) {
      throw new Error('A required fee product is unavailable or incorrectly configured');
    }
    return state;
  }

  async function write(path, body) {
    const response = await nativeFetch(`${root}cart/${path}.js`, { method: 'POST', headers, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error(`Could not ${path} required fees`);
  }

  async function reconcile() {
    for (let attempt = 0; attempt < 5; attempt++) {
      const state = await readState();
      const required = new Map();
      state.eligible.forEach(line => {
        const id = String(line.feeVariantId);
        required.set(id, (required.get(id) || 0) + 1);
      });
      const seen = new Set();
      const updates = {};
      state.fees.forEach(fee => {
        const id = String(fee.variantId);
        const quantity = seen.has(id) ? 0 : required.get(id) || 0;
        seen.add(id);
        if (fee.quantity !== quantity) updates[fee.key] = quantity;
      });
      const items = [];
      required.forEach((quantity, id) => {
        if (!seen.has(id)) items.push({ id, quantity });
      });
      if (!Object.keys(updates).length && !items.length) {
        if (alert) alert.hidden = true;
        hideExpressCheckout(state.eligible.length > 0);
        return state;
      }
      if (Object.keys(updates).length) await write('update', { updates });
      if (items.length) await write('add', { items });
      // Verify the resulting cart, including changed line keys and rejected quantities.
    }
    throw new Error('Required fees did not settle');
  }

  function hideExpressCheckout(required) {
    document.documentElement.toggleAttribute('data-labour-fee-cart', required);
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach(element => {
      if (required) {
        element.dataset.labourFeeHidden = 'true';
        element.hidden = true;
        element.style.display = 'none';
        element.setAttribute('inert', '');
      } else if (element.dataset.labourFeeHidden) {
        delete element.dataset.labourFeeHidden;
        element.hidden = false;
        element.style.removeProperty('display');
        element.removeAttribute('inert');
      }
    });
  }

  async function refreshUI(state) {
    const targets = [
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'main-cart-items', selector: '.js-contents' },
      { id: 'main-cart-footer', selector: '.js-contents' },
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-notification-button', section: 'cart-notification-button', selector: '.shopify-section' },
    ].filter(target => {
      const element = document.getElementById(target.id);
      target.section ||= element?.dataset.id;
      return element && target.section;
    });
    if (targets.length) {
      const html = await sections([...new Set(targets.map(target => target.section))]);
      targets.forEach(target => {
        const source = new DOMParser().parseFromString(html[target.section] || '', 'text/html').querySelector(target.selector);
        const element = document.getElementById(target.id);
        const destination = element?.querySelector(target.selector) || element;
        if (!source || !destination) throw new Error('Could not refresh cart totals');
        destination.innerHTML = source.innerHTML;
      });
    }
    document.querySelectorAll('cart-items, cart-drawer, cart-drawer-items, #main-cart-footer').forEach(element => {
      element.classList.toggle('is-empty', state.cart.item_count === 0);
    });
    hideExpressCheckout(state.eligible.length > 0);
  }

  function sync() {
    const result = enqueue(async () => {
      const state = await reconcile();
      await refreshUI(state);
      return state;
    });
    return result.catch(error => { showError(error); throw error; });
  }

  window.fetch = function (input, init) {
    if (!mutation(input, init?.method || input?.method || 'GET')) return nativeFetch(input, init);
    return enqueue(async () => {
      const response = await nativeFetch(input, init);
      try {
        const state = await reconcile();
        // Return fresh bundled sections to Dawn; the original add response predates the fee.
        // Keep add-response identity intact for the product-added notification.
        let payload;
        try { payload = await response.clone().json(); } catch (_) { /* non-JSON app response */ }
        if (response.ok && payload) {
          const rendered = payload.sections ? await sections(Object.keys(payload.sections)) : null;
          if ('item_count' in payload && Array.isArray(payload.items)) {
            payload = { ...payload, ...state.cart };
          } else {
            // An order discount triggered by the fee can change a product's line key.
            // Notifications must refer to the key in the newly rendered cart section.
            const currentKey = added => {
              const line = state.cart.items.find(item => item.key === added.key) || state.cart.items.find(item =>
                String(item.variant_id || item.id) === String(added.variant_id || added.id) &&
                JSON.stringify(item.properties || {}) === JSON.stringify(added.properties || {}) &&
                item.selling_plan_allocation?.selling_plan?.id === added.selling_plan_allocation?.selling_plan?.id
              );
              return line ? { ...added, key: line.key } : added;
            };
            payload = Array.isArray(payload.items) ? { ...payload, items: payload.items.map(currentKey) } : currentKey(payload);
          }
          if (rendered) payload.sections = rendered;
          // Dawn reads the old quantity inputs before replacing bundled sections itself.
          // Replacing them here would break removal of the last eligible product.
          if (!rendered) await refreshUI(state);
          const responseHeaders = new Headers(response.headers);
          responseHeaders.delete('Content-Length');
          return new Response(JSON.stringify(payload), { status: response.status, statusText: response.statusText, headers: responseHeaders });
        }
        await refreshUI(state);
      } catch (error) {
        // The original item may already be added. Preserve its response and show a retryable error;
        // checkout always performs a fresh reconciliation and will not continue on this error.
        showError(error);
      }
      return response;
    });
  };

  // Globo/jQuery quick add can use XMLHttpRequest rather than fetch.
  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  const xhrMutation = new WeakMap();
  XMLHttpRequest.prototype.open = function (method, url, ...args) {
    xhrMutation.set(this, mutation(url, method));
    return open.call(this, method, url, ...args);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (!xhrMutation.get(this)) return send.apply(this, args);
    let resolve;
    const complete = new Promise(done => { resolve = done; });
    activeXHR.add(complete);
    this.addEventListener('loadend', () => {
      sync().catch(() => {}).finally(() => {
        activeXHR.delete(complete);
        resolve();
      });
    }, { once: true });
    try { return send.apply(this, args); }
    catch (error) { activeXHR.delete(complete); resolve(); throw error; }
  };

  async function checkout(button) {
    if (checkingOut) return;
    checkingOut = true;
    button?.setAttribute('aria-busy', 'true');
    try {
      // Let Dawn's debounced quantity/note handlers submit edits made immediately before checkout.
      await new Promise(resolve => setTimeout(resolve, 350));
      do {
        await Promise.all([...activeXHR]);
        const current = queue;
        await current.catch(() => {});
        if (current === queue && !activeXHR.size) break;
      } while (true);
      const state = await sync();
      if (state.cart.item_count > 0) window.location.assign(`${root}checkout`);
    } catch (error) {
      showError(error);
    } finally {
      checkingOut = false;
      button?.removeAttribute('aria-busy');
    }
  }

  function isCheckoutLink(element) {
    if (!element?.matches('a[href]')) return false;
    const url = new URL(element.href, location.href);
    return url.origin === location.origin && /\/checkout\/?$/.test(url.pathname);
  }

  document.addEventListener('click', event => {
    const element = event.target.closest?.('[name="checkout"], a[href]');
    if (!element || (element.name !== 'checkout' && !isCheckoutLink(element))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(element);
  }, true);

  document.addEventListener('submit', event => {
    const form = event.target;
    // Covers keyboard submission and requestSubmit(), including cart checkout forms in app widgets.
    const submitter = event.submitter;
    const cartForm = new URL(form.action || location.href, location.href).pathname.replace(/\/$/, '') === cartURL.pathname;
    if (submitter?.name !== 'checkout' && !(cartForm && !submitter)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout(submitter);
  }, true);

  window.HerculesLabourFee = { sync };
  if (Number(script?.dataset.cartCount) > 0) sync().catch(() => {});
})();
